import { print } from 'graphql/index';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import { fileFragment, getIdsByFilterFileQuery, moveFilesAndFoldersFileQuery } from '~/shared/graphql-queries';
import childFoldersFileQuery from '~/shared/graphql-queries/files/child-folders-file-query';
import copyFilesToFolderFileMutation from '~/shared/graphql-queries/files/copy-files-to-folder-file-mutation';
import createManyFileMutation from '~/shared/graphql-queries/files/create-many-file-mutation';
import createOneFileMutation from '~/shared/graphql-queries/files/create-one-file-mutation';
import removeByPathFileMutation from '~/shared/graphql-queries/files/remove-by-path-file-mutation';
import removeManyFileMutation from '~/shared/graphql-queries/files/remove-many-file-mutation';
import updateByIdFileMutation from '~/shared/graphql-queries/files/update-by-id-file-mutation';
import updateFilesFolderFileMutation from '~/shared/graphql-queries/files/update-files-folder-file-mutation';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type PaginationFileResponse = Readonly<{
  data?: {
    paginationFileQuery?: {
      items: Record<string, unknown>[];
      pageInfo: PageInfoType;
    };
  };
}>;

type PaginationFileResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type PaginationFileArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  sort?: string;
  perPage?: number;
}>;

type GetIdsByFilterFileArgs = Readonly<{
  filter?: Record<string, unknown>;
  sort?: string;
}>;

type MoveFilesAndFoldersArgs = Readonly<{
  items: { id: string }[];
  targetFolderId: string;
}>;

type MoveFilesAndFoldersResult = Readonly<{
  movedCount: number;
  conflicts: string[];
  errors: string[];
}>;

type CopyFilesToFolderArgs = Readonly<{
  items: { id: string }[];
  targetFolderId: string;
}>;

type CopyFilesToFolderResult = Readonly<{
  copiedCount: number;
  errors: string[];
}>;

type GetChildFoldersArgs = Readonly<{
  parentId: string;
  search?: string | null;
}>;

type GetChildFoldersResult = Readonly<{
  childFolders: {
    id: string;
    name: string;
    isFolder: boolean;
    parentId?: string | null;
  }[];
}>;

type CreateManyFileArgs = Readonly<{
  records?: unknown[];
}>;

type UpdateByIdFileArgs = Readonly<{
  _id?: string;
  record?: Record<string, unknown>;
}>;

type CreateOneFileArgs = Readonly<{
  record?: Record<string, unknown>;
}>;

type RemoveManyFileArgs = Readonly<{
  ids?: string[];
}>;

type UpdateFilesFolderFileArgs = Readonly<{
  folder?: string;
  files?: unknown[];
}>;

type RemoveByPathFileArgs = Readonly<{
  files?: unknown[];
}>;

const transformFileResponse =
  (type: 'paginationFileQuery') =>
  (response: PaginationFileResponse): PaginationFileResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

export const filesTagTypes = ['files'] as const;

export const getFilesQuery = (builder: BuilderType) => {
  return {
    paginationFile: builder.query<PaginationFileResult, PaginationFileArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'file',
              sortType: 'SortEnum',
              fragment: fileFragment,
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? SORT_TYPE._ID_ASC,
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['files'],
      transformResponse: transformFileResponse('paginationFileQuery'),
    }),

    getIdsByFilterFile: builder.query<unknown, GetIdsByFilterFileArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(getIdsByFilterFileQuery),
          variables: {
            filter: args?.filter,
            sort: args?.sort ?? SORT_TYPE._ID_ASC,
          },
        }),
      }),
    }),

    moveFilesAndFolders: builder.mutation<MoveFilesAndFoldersResult, MoveFilesAndFoldersArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(moveFilesAndFoldersFileQuery),
          variables: {
            items: args?.items,
            targetFolderId: args?.targetFolderId,
          },
        }),
      }),
    }),

    copyFilesToFolder: builder.mutation<CopyFilesToFolderResult, CopyFilesToFolderArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(copyFilesToFolderFileMutation),
          variables: {
            items: args?.items,
            targetFolderId: args?.targetFolderId,
          },
        }),
      }),
    }),

    getChildFolders: builder.query<GetChildFoldersResult, GetChildFoldersArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(childFoldersFileQuery),
          variables: {
            parentId: args?.parentId,
            search: args?.search ?? null,
          },
        }),
      }),
    }),

    createManyFile: builder.mutation<unknown, CreateManyFileArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createManyFileMutation),
          variables: {
            records: args?.records,
          },
        }),
      }),
    }),

    updateByIdFile: builder.mutation<unknown, UpdateByIdFileArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateByIdFileMutation),
          variables: {
            _id: args?._id,
            record: args?.record,
          },
        }),
      }),
    }),

    createOneFile: builder.mutation<unknown, CreateOneFileArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneFileMutation),
          variables: {
            record: args?.record,
          },
        }),
      }),
    }),

    removeManyFile: builder.mutation<unknown, RemoveManyFileArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeManyFileMutation),
          variables: {
            ids: args?.ids,
          },
        }),
      }),
    }),

    updateFilesFolderFile: builder.mutation<unknown, UpdateFilesFolderFileArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateFilesFolderFileMutation),
          variables: {
            folder: args?.folder,
            files: args?.files,
          },
        }),
      }),
    }),

    removeByPathFile: builder.mutation<unknown, RemoveByPathFileArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeByPathFileMutation),
          variables: {
            files: args?.files,
          },
        }),
      }),
    }),
  };
};
